import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { useQuery } from '@apollo/client/react'
import getGroupUrl from 'helpers/getGroupUrl'

import { CameraIcon } from '@pleio/icons/camera'

import Button from 'js/components/Button'
import { CoverImage } from 'js/components/CoverImage'
import Flexer from 'js/components/Flexer/Flexer'
import { Col, Container, Row } from 'js/components/Grid'
import { H1, H4 } from 'js/components/Heading'
import HideVisually from 'js/components/HideVisually/HideVisually'
import ToggleTranslation from 'js/components/Item/components/ToggleTranslation'
import ItemActions from 'js/components/Item/ItemActions/ItemActions'
import ItemStatusTag from 'js/components/Item/ItemStatusTag'
import ItemSuggestedItems from 'js/components/Item/ItemSuggestedItems'
import ItemTags from 'js/components/ItemTags'
import Modal from 'js/components/Modal/Modal'
import Section from 'js/components/Section/Section'
import SocialShare from 'js/components/SocialShare/SocialShare'
import Text from 'js/components/Text/Text'
import TiptapView from 'js/components/Tiptap/TiptapView'
import { NotFound } from 'js/core/NotFound'
import { onlyTypename } from 'js/lib/helpers/onlyTypename'
import { useSiteStore } from 'js/lib/stores/SiteStore'
import ThemeProvider from 'js/theme/ThemeProvider'

import CommentList from '../../components/CommentList'
import Document from '../../components/Document'
import AttendeesActionsCard from '../components/AttendeesActionsCard'
import EventDate from '../components/EventDate'
import EventLink from '../components/EventLink'
import EventLocation from '../components/EventLocation'
import SendMessage from '../components/SendMessage'
import SlotsForm from '../components/SlotsForm'
import { EventActivities } from '../EventActivities'
import { AttendeesModal } from '../EventAttendees/components/AttendeesModal'
import getRefetchQueries from '../helpers/getRefetchQueries'

import { EventPageTabs } from './event-page.types'
import { EVENT_ITEM } from './EventItem.queries'
import styles from './EventPage.module.css'
import AddAttendee from './ItemActions/AddAttendee'
import InvitePeople from './ItemActions/InvitePeople/InvitePeople'

export const EventPage = () => {
    const { t } = useTranslation()
    const params = useParams()

    const { guid, groupGuid } = params

    const { site } = useSiteStore()

    const {
        integratedVideocallEnabled,
        showCustomTagsInDetail,
        showTagsInDetail,
    } = site

    const { loading, data } = useQuery(EVENT_ITEM, {
        variables: {
            guid,
            groupGuid,
        },
        fetchPolicy: 'cache-and-network',
    })

    // We are only interested when its an event
    const entity = onlyTypename(data?.entity, 'Event')
    const viewer = data?.viewer

    useEffect(() => {
        if (!loading && entity) {
            setIsTranslated(!!entity.localTitle)
        }
    }, [loading, entity])

    const [attendeesModal, setAttendeesModal] = useState<EventPageTabs>(null)

    const [showInvitePeopleModal, setShowInvitePeopleModal] = useState(false)
    const toggleInvitePeopleModal = () =>
        setShowInvitePeopleModal(!showInvitePeopleModal)

    const [showAddAttendeeModal, setShowAddAttendeeModal] = useState(false)
    const toggleAddAttendeeModal = () =>
        setShowAddAttendeeModal(!showAddAttendeeModal)

    const [showSendMessageModal, setShowSendMessageModal] = useState(false)
    const toggleSendMessageModal = () =>
        setShowSendMessageModal(!showSendMessageModal)

    const onAfterDelete = () => `${getGroupUrl(params)}/events`

    const [showSlotsModal, setShowSlotsModal] = useState(false)
    const toggleSlotsModal = () => {
        setShowSlotsModal(!showSlotsModal)
    }

    const [isTranslated, setIsTranslated] = useState(null)

    // FIXME: we should show Skeleton or at least we are retrieving the information
    if (loading) {
        return null
    }

    if (!entity) {
        return <NotFound />
    }

    const {
        localTitle,
        title,
        url,
        featured,
        parent,
        group,
        location,
        locationAddress,
        locationLink,
        hasChildren,
        statusPublished,
        canAttendWithEmail,
        startDate,
        endDate,
        source,
        localRichDescription,
        richDescription,
        tags,
        tagCategories,
        commentCount,
        suggestedItems,
        isTranslationEnabled,
        canEdit,
        videoCallEnabled,
        videoCallUrl,
    } = entity

    const hasTranslations = !!localTitle && isTranslationEnabled

    const translatedTitle = isTranslated ? localTitle : title
    const translatedRichDescription = isTranslated
        ? localRichDescription
        : richDescription

    const onEdit = `${getGroupUrl(params)}/events/edit/${guid}`
    const refetchQueries = getRefetchQueries(true, !!group)
    const containerTitle = group?.name
    const showLocation = !!location || !!locationAddress || !!locationLink

    const isPublished = statusPublished === 'published'

    const canEditItem = canEdit && statusPublished !== 'archived'

    // Not an activity or repeating event
    const canAddSubEvent = !parent && !entity?.rangeSettings?.type

    const canAttendSubEvents =
        !parent &&
        (entity?.requireAttendParent
            ? ['accept', 'online'].includes(entity?.isAttending)
            : true)

    const hasSuggestedItems = suggestedItems?.length > 0

    return (
        <ThemeProvider bodyColor="#FFFFFF">
            <Document title={translatedTitle} containerTitle={containerTitle} />
            <Section
                className={styles.wrapper}
                backgroundColor="white"
                style={{ paddingTop: 0 }}
            >
                <Container className="EventCoverContainer">
                    <CoverImage featured={featured} />
                </Container>
                <Container>
                    <Row $spacing={20} style={{ marginTop: '32px' }}>
                        <Col mobileLandscape={1 / 2} tabletUp={2 / 3}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div>
                                    <H1 as={groupGuid ? 'h2' : 'h1'}>
                                        {translatedTitle}
                                        <ItemStatusTag
                                            entity={entity}
                                            style={{
                                                marginTop: '8px',
                                            }}
                                        />
                                    </H1>

                                    {parent && (
                                        <Text size="small">
                                            <HideVisually>
                                                {t(
                                                    'entity-event.sub-event-of',
                                                )}{' '}
                                            </HideVisually>
                                            <Link to={parent.url}>
                                                {parent.title}
                                            </Link>
                                        </Text>
                                    )}
                                </div>
                                <ItemActions
                                    showEditButton
                                    entity={entity}
                                    onEdit={onEdit}
                                    onAfterDelete={onAfterDelete}
                                    canDuplicate
                                    refetchQueries={refetchQueries}
                                    customOptions={[
                                        [
                                            ...(canEdit
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.attendees',
                                                          ),
                                                          onClick: () =>
                                                              setAttendeesModal(
                                                                  'going',
                                                              ),
                                                      },
                                                  ]
                                                : []),
                                            ...(canEdit && site.eventExport
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.export-attendees',
                                                          ),
                                                          url: `/exporting/event/${guid}`,
                                                          download: true,
                                                      },
                                                  ]
                                                : []),
                                            ...(canEdit
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.guest-list',
                                                          ),
                                                          url: `${url}/guest-list`,
                                                          newWindow: true,
                                                      },
                                                  ]
                                                : []),
                                        ],
                                        [
                                            ...(canEdit
                                                ? [
                                                      {
                                                          label: t(
                                                              'global.send-message',
                                                          ),
                                                          onClick:
                                                              toggleSendMessageModal,
                                                      },
                                                  ]
                                                : []),
                                            ...(canEdit
                                                ? [
                                                      {
                                                          label: t(
                                                              'global.invite-people',
                                                          ),
                                                          onClick:
                                                              toggleInvitePeopleModal,
                                                      },
                                                  ]
                                                : []),
                                            ...(canEdit && canAttendWithEmail
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.add-attendee',
                                                          ),
                                                          onClick:
                                                              toggleAddAttendeeModal,
                                                      },
                                                  ]
                                                : []),
                                        ],
                                        [
                                            ...(canEditItem && hasChildren
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.activity-slots',
                                                          ),
                                                          onClick:
                                                              toggleSlotsModal,
                                                      },
                                                  ]
                                                : []),
                                            ...(canEditItem && canAddSubEvent
                                                ? [
                                                      {
                                                          label: t(
                                                              'entity-event.create-activity',
                                                          ),
                                                          url: `${getGroupUrl(
                                                              params,
                                                          )}/events/add/${guid}`,
                                                      },
                                                  ]
                                                : []),
                                        ],
                                    ]}
                                    style={{ marginLeft: 'auto' }}
                                />
                            </div>
                            <Row $growContent>
                                <Col tabletUp={showLocation ? 1 / 2 : 1}>
                                    <EventDate
                                        startDate={startDate}
                                        endDate={endDate}
                                        size="large"
                                        style={{ marginTop: '20px' }}
                                    />
                                </Col>
                                {showLocation && (
                                    <Col
                                        tabletUp={1 / 2}
                                        style={{ marginTop: '20px' }}
                                    >
                                        <EventLocation
                                            entity={entity}
                                            size="normal"
                                        />
                                    </Col>
                                )}
                            </Row>

                            {source && <EventLink source={source} />}

                            {videoCallEnabled &&
                                videoCallUrl &&
                                integratedVideocallEnabled && (
                                    <Flexer
                                        justifyContent="flex-start"
                                        style={{ marginTop: 20 }}
                                    >
                                        <Button
                                            size="large"
                                            variant="secondary"
                                            as="a"
                                            href={videoCallUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <CameraIcon
                                                style={{
                                                    margin: '0 8px 0 -2px',
                                                }}
                                            />
                                            {t('global.video-call')}
                                            <HideVisually>
                                                {t(
                                                    'global.opens-in-new-window',
                                                )}
                                            </HideVisually>
                                        </Button>
                                    </Flexer>
                                )}

                            <TiptapView
                                content={translatedRichDescription}
                                style={{ marginTop: '24px' }}
                            />

                            {hasTranslations && (
                                <ToggleTranslation
                                    isTranslated={isTranslated}
                                    setIsTranslated={setIsTranslated}
                                    style={{ marginTop: '12px' }}
                                />
                            )}

                            <ItemTags
                                showCustomTags={showCustomTagsInDetail}
                                showTags={showTagsInDetail}
                                customTags={tags}
                                tagCategories={tagCategories}
                                style={{ marginTop: '24px' }}
                            />

                            {hasChildren && (
                                <EventActivities
                                    guid={entity.guid}
                                    startDate={entity.startDate}
                                    endDate={entity.endDate}
                                    isDisabled={!canAttendSubEvents}
                                    summaryEvents={entity.children}
                                    // FIXME: get rid of these props drilling setup
                                    viewer={viewer}
                                    // FIXME: let the operation decide what to do
                                    refetchQueries={refetchQueries}
                                />
                            )}

                            {isPublished && site.enableSharing && (
                                <SocialShare />
                            )}
                        </Col>
                        <Col mobileLandscape={1 / 2} tabletUp={1 / 3}>
                            <AttendeesActionsCard
                                entity={entity}
                                viewer={viewer}
                                site={site}
                            />
                        </Col>
                    </Row>
                </Container>

                <Container size="large">
                    <Row style={{ marginTop: '32px' }}>
                        <Col tabletUp={2 / 3}>
                            <H4 as="h2">
                                {t('comments.count-comments', {
                                    count: commentCount,
                                })}
                            </H4>
                            <CommentList
                                viewer={viewer}
                                entity={entity}
                                refetchQueries={refetchQueries}
                            />
                        </Col>
                        {hasSuggestedItems && (
                            <Col tabletUp={1 / 3}>
                                <ItemSuggestedItems entity={entity} />
                            </Col>
                        )}
                    </Row>
                </Container>
            </Section>
            <AttendeesModal
                tab={attendeesModal}
                setTab={setAttendeesModal}
                entity={entity}
            />
            <Modal
                size="normal"
                isVisible={showInvitePeopleModal}
                onClose={toggleInvitePeopleModal}
                title={t('global.invite-people')}
                showCloseButton
                containHeight
            >
                <InvitePeople guid={guid} />
            </Modal>
            <Modal
                size="small"
                isVisible={showAddAttendeeModal}
                onClose={toggleAddAttendeeModal}
                title={t('entity-event.add-attendee')}
                showCloseButton
            >
                <AddAttendee guid={guid} onClose={toggleAddAttendeeModal} />
            </Modal>
            <Modal
                size="normal"
                title={t('global.send-message')}
                showCloseButton
                isVisible={showSendMessageModal}
                onClose={toggleSendMessageModal}
                containHeight
            >
                <SendMessage entity={entity} onClose={toggleSendMessageModal} />
            </Modal>
            {hasChildren && (
                <Modal
                    isVisible={showSlotsModal}
                    showCloseButton
                    onClose={toggleSlotsModal}
                    title={t('entity-event.activity-slots')}
                    size="normal"
                >
                    <SlotsForm entity={entity} onClose={toggleSlotsModal} />
                </Modal>
            )}
        </ThemeProvider>
    )
}
